import React from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import HBText from '../HBText/HBText';
import HBLinkButton from '../HBLinkButton/HBLinkButton';
import HBButton from '../HBButton/HBButton';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { Close16, ArrowRight16 } from '@honeybook/icons';
import {
    IHBCalloutBannerProps,
    THBCalloutBannerIllustrationAlignment,
    THBCalloutBannerStyles
} from './HBCalloutBanner.types';
import { useHBCalloutBannerStyles } from './useHBCalloutBannerStyles';
import {
    StyledMediaImage,
    StyledMediaVideo
} from '../HBMediaElement/HBMediaElement';

const HBCalloutBanner = ({
    type,
    theme,
    size = 'sm',
    title,
    description,
    illustration,
    illustrationAspectRatio = '1:1',
    illustrationOrientation = 'landscape',
    illustrationAlignment = 'cover',
    illustrationPadding = false,
    primaryAction,
    primaryActionType = 'button',
    secondaryAction,
    onDismiss,
    className,
    testIdName,
    testId
}: IHBCalloutBannerProps) => {
    const calloutBannerStyles: THBCalloutBannerStyles =
        useHBCalloutBannerStyles(
            type,
            theme,
            size,
            illustrationAspectRatio,
            illustrationOrientation
        );

    const isPrimaryActionTypeLinkButton = primaryActionType === 'link-button';

    const bannerTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'container',
        componentName: 'HBCalloutBanner'
    });

    const primaryActionTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'primary-action',
        componentName: isPrimaryActionTypeLinkButton
            ? 'HBLinkButton'
            : 'HBButton'
    });

    const secondaryActionTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'secondary-action',
        componentName: 'HBButton'
    });

    const closeIconTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'close-icon',
        componentName: 'Close16'
    });

    return (
        <Container
            $backgroundColor={calloutBannerStyles.backgroundColor}
            $shadow={calloutBannerStyles.shadow}
            $accentLineColor={calloutBannerStyles.accentLineColor}
            className={className}
            data-testid={bannerTestId}
        >
            <Content $padding={calloutBannerStyles.padding}>
                <Title
                    type={calloutBannerStyles.titleType}
                    color={calloutBannerStyles.textColor}
                >
                    {title}
                </Title>
                <Description
                    type={calloutBannerStyles.descriptionType}
                    color={calloutBannerStyles.textColor}
                >
                    {description}
                </Description>
                <Footer>
                    {isPrimaryActionTypeLinkButton ? (
                        <LinkButton
                            linkButtonTheme={
                                calloutBannerStyles.primaryActionLinkButtonTheme
                            }
                            size={calloutBannerStyles.linkButtonSize}
                            icon={
                                <Arrow color={calloutBannerStyles.iconColor} />
                            }
                            iconPosition="end"
                            testIdName={primaryActionTestId}
                            {...primaryAction}
                        >
                            {primaryAction.text}
                        </LinkButton>
                    ) : (
                        <>
                            <HBButton
                                buttonTheme={
                                    calloutBannerStyles.primaryActionButtonTheme
                                }
                                size={calloutBannerStyles.buttonSize}
                                testIdName={primaryActionTestId}
                                {...primaryAction}
                            >
                                {primaryAction.text}
                            </HBButton>
                            {secondaryAction && (
                                <HBButton
                                    buttonTheme={
                                        calloutBannerStyles.secondaryActionButtonTheme
                                    }
                                    size={calloutBannerStyles.buttonSize}
                                    testIdName={secondaryActionTestId}
                                    {...secondaryAction}
                                >
                                    {secondaryAction.text}
                                </HBButton>
                            )}
                        </>
                    )}
                </Footer>
            </Content>
            {illustration && (
                <IllustrationContainer
                    $illustrationPlaceholder={
                        calloutBannerStyles.illustrationPlaceholder
                    }
                    $illustrationAlignment={illustrationAlignment}
                    $illustrationPadding={illustrationPadding}
                >
                    {illustration}
                </IllustrationContainer>
            )}
            {onDismiss && (
                <Close
                    color={calloutBannerStyles.closeColor}
                    onClick={onDismiss}
                    data-testid={closeIconTestId}
                />
            )}
        </Container>
    );
};

export default HBCalloutBanner;

const Container = styled.div<{
    $backgroundColor: string;
    $shadow: boolean;
    $accentLineColor: string;
}>`
    display: flex;
    border-radius: ${vars.radius.xs};
    ${({ $backgroundColor, $shadow, $accentLineColor }) => css`
        position: relative;
        background-color: ${$backgroundColor};
        ${!!$shadow &&
        css`
            box-shadow: ${vars.shadow['10']};
        `};
        border-left: 4px solid ${$accentLineColor};
    `};
`;

const Content = styled.div<{
    $padding: number;
}>`
    ${({ $padding }) => css`
        padding: ${$padding}px;
    `};
    flex-grow: 1;
`;

const Title = styled(HBText)`
    display: block;
`;

const Description = styled(HBText)`
    margin: 4px 0;
    display: inline-block;
`;

const Footer = styled.div`
    margin-top: 8px;
    display: flex;
    align-self: start;
    gap: 8px;
`;

const LinkButton = styled(HBLinkButton)`
    height: 18px;
`;

const Arrow = styled(ArrowRight16)`
    margin-left: -6px;
    line-height: 0;
    display: block;
`;

const IllustrationContainer = styled.div<{
    $illustrationPlaceholder: number;
    $illustrationAlignment: THBCalloutBannerIllustrationAlignment;
    $illustrationPadding: boolean;
}>`
    width: min(
        35%,
        ${({ $illustrationPlaceholder }) => $illustrationPlaceholder}px
    );
    flex-shrink: 0;

    & ${StyledMediaImage}, & ${StyledMediaVideo} {
        position: absolute;
        right: 0;
        max-width: min(
            35%,
            ${({ $illustrationPlaceholder }) => $illustrationPlaceholder}px
        );
        border-radius: 0 ${vars.radius.xs} ${vars.radius.xs} 0;

        ${({ $illustrationAlignment, $illustrationPadding }) => css`
            ${$illustrationAlignment === 'start' &&
            css`
                top: 0;
                max-height: 100%;
                object-fit: contain;
            `}
            ${$illustrationAlignment === 'end' &&
            css`
                bottom: 0;
                max-height: 100%;
                object-fit: contain;
            `}
            ${$illustrationAlignment === 'center' &&
            css`
                height: 100%;
                max-height: 100%;
                object-fit: contain;
            `}
            ${$illustrationAlignment === 'cover' &&
            css`
                object-fit: cover;
                height: 100%;
            `}
            ${$illustrationPadding &&
            css`
                padding: 16px;
            `}
        `}
    }
`;

const Close = styled(Close16)`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;
